import { useState, useEffect, useMemo, useRef } from 'react';
import { Search, X, ChevronDown, Building2 } from '../../icons/wp-icons';
import Modal from '../Common/Modal';
import { createAnfrage } from '../../api/anfrageApi';
import { getAllKunden } from '../../api/kundenApi';
import { objektApi } from '../../api/objektApi';
import toast from 'react-hot-toast';

const KATEGORIEN = ['Termin', 'Angebot', 'Info', 'Rückruf'];

export default function NeueAnfrageModal({ isOpen, onClose, onCreated }) {
  const [kunden, setKunden] = useState([]);
  const [loadingKunden, setLoadingKunden] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const kundenDropdownRef = useRef(null);
  const [formData, setFormData] = useState({ betreff: '', nachricht: '', kategorie: 'Info', objektId: '' });
  const [formErrors, setFormErrors] = useState({});
  const [selectedKundeObjekte, setSelectedKundeObjekte] = useState([]);
  const [loadingObjekte, setLoadingObjekte] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadKunden = async () => {
    try {
      setLoadingKunden(true);
      const data = await getAllKunden();
      setKunden(data);
    } catch {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoadingKunden(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setFormData({ betreff: '', nachricht: '', kategorie: 'Info', objektId: '' });
    setFormErrors({});
    setSelectedKunde(null);
    setSelectedKundeObjekte([]);
    setKundenSuche('');
    setShowKundenDropdown(false);
    if (kunden.length === 0) loadKunden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(e.target)) {
        setShowKundenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const gefilterteKunden = useMemo(() => {
    if (!kundenSuche.trim()) return kunden;
    const s = kundenSuche.toLowerCase();
    return kunden.filter(k =>
      k.name?.toLowerCase().includes(s) ||
      k.email?.toLowerCase().includes(s) ||
      k.telefon?.toLowerCase().includes(s)
    );
  }, [kunden, kundenSuche]);

  const handleKundeSelect = async (kunde) => {
    setSelectedKunde(kunde);
    setShowKundenDropdown(false);
    setKundenSuche('');
    setFormData(prev => ({ ...prev, objektId: '' }));
    if (formErrors.kundeId) setFormErrors(prev => ({ ...prev, kundeId: '' }));
    if (kunde.kundenTyp === 'HAUSVERWALTUNG') {
      setLoadingObjekte(true);
      try {
        const res = await objektApi.getByKunde(kunde.id);
        setSelectedKundeObjekte(res.data || []);
      } catch {
        setSelectedKundeObjekte([]);
      } finally {
        setLoadingObjekte(false);
      }
    } else {
      setSelectedKundeObjekte([]);
    }
  };

  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setKundenSuche('');
    setSelectedKundeObjekte([]);
    setFormData(prev => ({ ...prev, objektId: '' }));
  };

  const handleClose = () => {
    setFormErrors({});
    setSelectedKunde(null);
    setKundenSuche('');
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!selectedKunde) errors.kundeId = 'Bitte wählen Sie einen Kunden aus';
    if (!formData.betreff.trim()) errors.betreff = 'Betreff ist erforderlich';
    if (!formData.nachricht.trim()) errors.nachricht = 'Nachricht ist erforderlich';
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    try {
      const newAnfrage = await createAnfrage({
        kundeId: selectedKunde.id,
        betreff: formData.betreff,
        nachricht: formData.nachricht,
        kategorie: formData.kategorie,
        ...(formData.objektId && { objektId: formData.objektId })
      });
      toast.success('Anfrage erstellt');
      handleClose();
      onCreated?.(newAnfrage);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Neue Anfrage">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Kunde <span className="text-danger">*</span>
          </label>
          <div className="relative" ref={kundenDropdownRef}>
            {selectedKunde ? (
              <div className={`flex items-center justify-between w-full px-3 sm:px-4 py-2.5 border rounded-lg bg-gray-50 ${
                formErrors.kundeId ? 'border-danger' : 'border-border'
              }`}>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-sm sm:text-base block truncate">{selectedKunde.name}</span>
                  {selectedKunde.email && (
                    <span className="text-text-muted text-xs sm:text-sm block truncate">{selectedKunde.email}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleKundeRemove}
                  className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                  disabled={isSaving}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={kundenSuche}
                  onChange={(e) => { setKundenSuche(e.target.value); setShowKundenDropdown(true); }}
                  onFocus={() => setShowKundenDropdown(true)}
                  placeholder="Kunde suchen..."
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
                    formErrors.kundeId ? 'border-danger' : 'border-border'
                  }`}
                  disabled={isSaving || loadingKunden}
                />
                <button
                  type="button"
                  onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  disabled={isSaving || loadingKunden}
                >
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                </button>
              </div>
            )}
            {showKundenDropdown && !selectedKunde && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {loadingKunden ? (
                  <div className="px-4 py-3 text-text-muted text-center">Lädt Kunden...</div>
                ) : gefilterteKunden.length === 0 ? (
                  <div className="px-4 py-3 text-text-muted text-center">
                    {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                  </div>
                ) : (
                  gefilterteKunden.map(kunde => (
                    <button
                      key={kunde.id}
                      type="button"
                      onClick={() => handleKundeSelect(kunde)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                    >
                      <div className="font-medium">{kunde.name}</div>
                      <div className="text-sm text-text-muted flex gap-3">
                        {kunde.email && <span>{kunde.email}</span>}
                        {kunde.telefon && <span>{kunde.telefon}</span>}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {formErrors.kundeId && <p className="text-danger text-sm mt-1">{formErrors.kundeId}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Betreff <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={formData.betreff}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, betreff: e.target.value }));
              if (formErrors.betreff) setFormErrors(prev => ({ ...prev, betreff: '' }));
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              formErrors.betreff ? 'border-danger' : 'border-border'
            }`}
            placeholder="z.B. Kostenvoranschlag Fensterscheibe"
            disabled={isSaving}
          />
          {formErrors.betreff && <p className="text-danger text-sm mt-1">{formErrors.betreff}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Kategorie</label>
          <div className="flex gap-2 flex-wrap">
            {KATEGORIEN.map((kat) => (
              <button
                key={kat}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, kategorie: kat }))}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  formData.kategorie === kat
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                disabled={isSaving}
              >
                {kat}
              </button>
            ))}
          </div>
        </div>

        {selectedKunde?.kundenTyp === 'HAUSVERWALTUNG' && (
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-purple-500" />
              Objekt
            </label>
            {loadingObjekte ? (
              <div className="text-sm text-text-muted py-2">Objekte werden geladen...</div>
            ) : selectedKundeObjekte.length > 0 ? (
              <select
                value={formData.objektId}
                onChange={(e) => setFormData(prev => ({ ...prev, objektId: e.target.value }))}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isSaving}
              >
                <option value="">Kein Objekt zugeordnet</option>
                {selectedKundeObjekte.filter(o => o.aktiv !== false).map(obj => (
                  <option key={obj.id} value={obj.id}>
                    {obj.name || obj.strasse}{obj.ort ? `, ${obj.ort}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-text-muted py-1">Keine Objekte vorhanden</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">
            Nachricht <span className="text-danger">*</span>
          </label>
          <textarea
            value={formData.nachricht}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, nachricht: e.target.value }));
              if (formErrors.nachricht) setFormErrors(prev => ({ ...prev, nachricht: '' }));
            }}
            rows="5"
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              formErrors.nachricht ? 'border-danger' : 'border-border'
            }`}
            placeholder="Beschreiben Sie die Anfrage..."
            disabled={isSaving}
          />
          {formErrors.nachricht && <p className="text-danger text-sm mt-1">{formErrors.nachricht}</p>}
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            {isSaving ? 'Wird erstellt...' : 'Erstellen'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
            disabled={isSaving}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </Modal>
  );
}
